<script>
import OrderBlock from "@/views/trips/edit/blocks/OrderBlock.vue";
import TravelOrderBlock from "@/views/trips/edit/blocks/TravelOrderBlock.vue";
import RouteBlock from "@/views/trips/edit/blocks/RouteBlock.vue";
import AddOrderDialog from "@/views/trips/edit/dialogs/AddOrderDialog.vue";
import ReorderOrderStops from "@/views/trips/edit/dialogs/ReorderOrderStops.vue";
import { OrdersMixin } from "@/views/orders/orders.mixin";
import AddReloadStopDialog from "@/views/trips/edit/dialogs/AddReloadStopDialog.vue";
import BaseView from "@/views/BaseView.vue";
import OmniDialog from "@/components/OmniDialog.vue";
import { TravelOrderStatus } from "@/data/trip";
import { mapActions } from "pinia";
import { useSnackbarStore } from "@/store/snackbar.store";
import RouteStopBlock from "@/views/trips/edit/blocks/RouteStopBlock.vue";
import OrderStopBlock from "@/views/trips/edit/blocks/OrderStopBlock.vue";
import TripInfo from "@/views/trips/edit/blocks/TripInfo.vue";
import { TripManager } from "@/views/trips/edit/trip-manager";
import { TripGrid } from "@/views/trips/edit/trip-grid";
import { useTrip } from "@/composables/index.js";
import { useError } from "@/composables/useError.js";
import { TRUCK_ID } from "@/data/query.ts";
import TooltipButton from "@/components/controls/TooltipButton.vue";
import { useDisableRules } from "@/composables/reactive/useDisableRules.ts";

const { isTravelOrderCancelled } = useTrip();
const { showAPIError } = useError();
const { createTripDispatchDisableRules } = useDisableRules();

export default {
  name: "TripEdit",
  components: {
    TooltipButton,
    TravelOrderBlock,
    TripInfo,
    OrderStopBlock,
    RouteStopBlock,
    OmniDialog,
    AddReloadStopDialog,
    ReorderOrderStops,
    AddOrderDialog,
    RouteBlock: RouteBlock,
    OrderBlock,
  },
  mixins: [BaseView, OrdersMixin],
  data() {
    return {
      orders: [],
      tripStops: [],
      deliveryNum: 0,
      pickupNum: 0,

      addOrderDialog: false,
      reorderOrderStopsDialog: false,

      addReloadStopDialog: false,
      reloadStopPosition: 1,

      tripManager: null,
      tripGrid: null,
      trip: null,
      tripFromAPI: null,
      reloadOrderStop: null,
      loading: false,
      saveLoading: false,
    };
  },
  computed: {
    orderFreights() {
      return this.tripManager.orders.flatMap((o) => o.freights);
    },
    cancelEnabled() {
      return this.$route.params.id && this.$route.params.id !== "new";
    },
    gridStyle() {
      return this.tripGrid.gridStyle;
    },
    stopsContainerStyle() {
      return this.tripGrid.stopsContainerStyle;
    },
    travelOrdersByOrder() {
      return this.tripManager.travelOrdersByOrder;
    },
    orderStops() {
      return this.trip.orders.flatMap((order) => order.tripStops);
    },
    routes() {
      return this.trip?.routes || [];
    },
    saveAndDispatchRules() {
      return createTripDispatchDisableRules(this.tripManager.routes, true);
    },
  },
  async created() {
    this.loading = true;
    this.tripManager = new TripManager(this.$api, this.$route);
    this.tripGrid = new TripGrid(this.tripManager);
    await this.tripManager.load();
    if (this.tripManager.shouldShowReorderStopsDialog) {
      this.showReorderStopsDialog();
    }

    this.trip = this.tripManager.trip;

    this.tripFromAPI = { ...this.trip };
    this.loading = false;
  },
  methods: {
    ...mapActions(useSnackbarStore, ["showSnackbarSuccess", "showSnackbarError"]),
    addOrder(order) {
      this.tripManager.addOrder(order);
      this.addOrderDialog = false;
      this.showReorderStopsDialog();
    },
    removeOrder(order) {
      // remove order from orders list
      const index = this.orders.indexOf(order);
      if (index !== -1) {
        this.orders.splice(index, 1);
      }

      // remove order stops
      this.tripStops = this.tripStops.filter((os) => os.orderId !== order.id);
    },
    showReorderStopsDialog() {
      this.reorderOrderStopsDialog = true;
    },
    closeReorderStopsDialog() {
      this.reorderOrderStopsDialog = false;
      this.tripManager.copyStopPositionsFromOrder();
    },
    getPreviousOrderStop(index) {
      for (let i = index; i >= 0; i--) {
        if (!this.orderStops[i].isReload) {
          return this.orderStops[i];
        }
      }
      return null;
    },
    showReloadStopDialog(index) {
      this.reloadOrderStop = this.getPreviousOrderStop(index);

      if (!this.reloadOrderStop) {
        return;
      }

      this.addReloadStopDialog = true;
      this.reloadStopPosition = index + 2;
    },
    addReloadStop(reloadStop) {
      this.tripManager.addReloadStop(reloadStop, this.reloadOrderStop.id);
      this.addReloadStopDialog = false;
    },
    async save() {
      const item = { routes: this.tripManager.preparedTripForAPI(this.tripManager.routes) };
      // add trip id for redispatch and save exists trip
      if (this.$route.params.id !== "new") {
        item.id = this.$route.params.id;
      }

      this.saveLoading = true;
      const resp = await this.$api.trips.upsertTrip(item);

      if (resp.success) {
        this.toView(resp.data);
        this.showSnackbarSuccess(this.$t("general.saved"));
      } else {
        showAPIError(resp, this.$t("general.save-error"));
      }

      this.saveLoading = false;
    },
    setTruck(route, truck) {
      if (truck) {
        route.truck = truck;
        route.truckId = truck.id;
      } else {
        route.truck = null;
        route.truckId = null;
      }
    },
    toView(updatedTrip = null) {
      if (!updatedTrip) {
        this.$router.push({
          name: "trip-view",
          params: { id: this.$route.params.id },
        });
        return;
      }

      const truckIds = updatedTrip.routes.flatMap((route) => route.truckId);

      this.$router.replace({
        name: "trip-view",
        params: { id: updatedTrip.id || this.$route.params.id },
        query: { [TRUCK_ID]: truckIds },
      });
    },
    isRouteStopCancelled(route, stop) {
      if (!stop) {
        return false;
      }

      const relatedTravelOrders = route.travelOrders.filter((to) => {
        return to?.tempId && stop.travelOrderIds?.includes(to.tempId);
      });

      return Boolean(relatedTravelOrders.length && relatedTravelOrders.every((to) => isTravelOrderCancelled(to)));
    },
    showAddOrderDialog() {
      this.addOrderDialog = true;
    },
    addEmptyRoute() {
      const copyFreights = this.tripManager.routes.every((r) =>
        r.travelOrders.every((to) =>
          [TravelOrderStatus.cancelled, TravelOrderStatus.dryRun, TravelOrderStatus.waitingForDryRun].includes(
            to.status,
          ),
        ),
      );
      this.tripManager.addRoute(null, copyFreights);
    },
    getRouteStop(orderStop, route) {
      return route.routeStops.find((rs) => rs.orderStopId === orderStop.id);
    },
    getRouteReloadStops(orderStop, route) {
      return route.routeStops.filter((rs) => rs.reloadStopId === orderStop.id).sort((a, b) => a.position - b.position);
    },
    changeRoute(route, stop, freights) {
      this.tripManager.updateRouteStop(stop);

      this.$nextTick(() => {
        this.tripManager.updateFreights(route, freights, stop);
      });

      this.$nextTick(() => {
        this.tripManager.addReloadFreights(stop);
      });

      this.$nextTick(() => {
        this.tripManager.setTravelOrderIdsForRouteStop(route, stop);
      });
    },
    onTravelOrderUpdate(route, travelOrder) {
      this.tripManager.updateRouteStatus(route);

      this.$nextTick(() => {
        this.tripManager.editTravelOrder(travelOrder);
      });

      this.$nextTick(() => {
        this.tripManager.editOrderByTravelOrder(travelOrder);
      });
    },
    getTravelOrderFromApi(routeId, travelOrderIndex) {
      return this.tripFromAPI.routes.find((route) => route.id === routeId)?.travelOrders[travelOrderIndex] || null;
    },
    getTORouteStops(route, travelOrder) {
      return route.routeStops.filter((routeStop) => routeStop?.order?.id === travelOrder.orderId);
    },
  },
};
</script>

<template>
  <v-container :fluid="true" class="pt-8 pb-10 pl-6 pr-6">
    <div class="pb-15">
      <div :style="gridStyle" class="stops-grid">
        <trip-info
          :orders="tripManager.orders"
          @add:order="showAddOrderDialog"
          @add:route="addEmptyRoute"
          @edit:stops-order="showReorderStopsDialog"
        ></trip-info>
        <order-block
          v-for="order in tripManager.orders"
          :key="order.id"
          :can-remove="tripManager.orders.length > 1"
          :order="order"
          class="mb-4"
          @remove="removeOrder"
        />
        <div
          :style="stopsContainerStyle"
          class="trips-block bg-white pa-4 pt-0 rounded-lg position-relative rounded-tl-0 rounded-tr-0 rounded-bl-0 rounded-br-0"
        >
          <div class="trip-block__header d-flex align-center justify-space-between my-2">
            <span class="text-grey-darken-2 font-weight-medium">Trip stops</span>
          </div>
          <template v-for="(stop, i) in tripManager.tripStops" :key="stop.id">
            <order-stop-block :order-freights="orderFreights" :routes="tripManager.routes" :stop="stop" />
            <div v-if="i < tripManager.tripStops.length - 1" class="plus-block text-center">
              <v-btn
                class="text-primary plus-btn"
                icon="mdi-plus"
                size="small"
                variant="flat"
                @click="showReloadStopDialog(i)"
              />
            </div>
          </template>
        </div>
        <template v-for="(route, i) in tripManager.routes" :key="i">
          <route-block
            v-model="tripManager.routes[i]"
            :can-remove="tripManager.routes.length > 1"
            :orders="tripManager.orders"
            class="mb-4"
            @attach="(truck) => setTruck(tripManager.routes[i], truck)"
            @remove-truck="setTruck(tripManager.routes[i], null)"
            @remove-route="tripManager.routes.splice(i, 1)"
          />

          <template v-if="!loading">
            <template v-for="(travelOrder, j) in route.travelOrders" :key="j">
              <travel-order-block
                v-model="route.travelOrders[j]"
                :route-stops="getTORouteStops(route, route.travelOrders[j])"
                :routes="tripManager.routes"
                :travel-order-from-api="getTravelOrderFromApi(route.id, j)"
                :travel-orders-by-orders="travelOrdersByOrder"
                :trip-id="$route.params.id"
                :truck="route.truck"
                class="mb-4"
                @update:model-value="(to) => onTravelOrderUpdate(route, to)"
              />
            </template>
          </template>

          <div
            v-if="route.routeStops.length > 0"
            :style="stopsContainerStyle"
            class="trip-block bg-white pa-4 rounded-lg position-relative rounded-tl-0 rounded-tr-0 rounded-bl-0 rounded-br-0"
            data-qa="route-stops"
          >
            <div class="trip-block__header d-flex align-center justify-space-between mb-3">
              <span class="text-grey-darken-2 font-weight-medium">Route stops</span>
            </div>
            <template v-for="(orderStop, k) in tripManager.tripStops" :key="k">
              <div v-if="orderStop.isReload" class="reload-stop-list">
                <route-stop-block
                  v-for="reloadStop in getRouteReloadStops(orderStop, route)"
                  :key="reloadStop.id"
                  :disabled="isRouteStopCancelled(route, reloadStop)"
                  :order-freights="orderFreights"
                  :route="route"
                  :route-stop="reloadStop"
                  :routes="tripManager.routes"
                  :trip-from-api="tripFromAPI"
                  :trip-manager="tripManager"
                  :trip-stops="tripManager.tripStops"
                  @changed:route="(stop, freights) => changeRoute(route, stop, freights)"
                />
              </div>
              <route-stop-block
                v-else-if="getRouteStop(orderStop, route)"
                :allow-edit-usage="tripManager.routes.length > 1"
                :disabled="isRouteStopCancelled(route, getRouteStop(orderStop, route))"
                :is-reload="orderStop.isReload"
                :order-freights="orderFreights"
                :route="route"
                :route-stop="getRouteStop(orderStop, route)"
                :routes="tripManager.routes"
                :trip-from-api="tripFromAPI"
                :trip-manager="tripManager"
                :trip-stops="tripManager.tripStops"
                @changed:route="(stop, freights) => changeRoute(route, stop, freights)"
              />
              <div v-else></div>
              <!-- Empty div is necessary for correct grid work -->
              <div v-if="k < tripManager.tripStops.length - 1"></div>
            </template>
          </div>
        </template>
      </div>

      <div class="d-flex justify-end pa-5 bg-white user-info__buttons">
        <v-btn
          v-if="cancelEnabled"
          id="cancel-btn"
          class="pr-6 pl-6 font-weight-600 text-uppercase"
          color="primary"
          height="36"
          variant="text"
          @click="() => toView()"
        >
          Cancel
        </v-btn>

        <tooltip-button :rules="saveAndDispatchRules">
          <template #default="{ disabled }">
            <v-btn
              id="save-btn"
              :disabled="disabled"
              :loading="saveLoading"
              class="pr-6 pl-6 font-weight-600 text-uppercase"
              color="primary"
              height="36"
              variant="flat"
              @click="() => save()"
            >
              <template v-if="disabled">Save</template>
              <template v-else>Save and dispatch</template>
            </v-btn>
          </template>
        </tooltip-button>
      </div>
    </div>

    <add-order-dialog
      v-model="addOrderDialog"
      :orders="tripManager.orders"
      :trip-stops="tripStops"
      @change="addOrder"
    />

    <add-reload-stop-dialog
      v-model="addReloadStopDialog"
      :position="reloadStopPosition"
      :trip-stops="tripManager.tripStops"
      @add="addReloadStop"
    />

    <omni-dialog
      v-model="reorderOrderStopsDialog"
      primary-action-label="Save"
      title="Stops order"
      width="585"
      @primary-action:click="closeReorderStopsDialog"
    >
      <reorder-order-stops v-model="tripManager.tripStops" :orders="tripManager.orders" :routes="tripManager.routes" />
    </omni-dialog>
  </v-container>
</template>

<style lang="scss" scoped>
.stops-grid {
  display: grid;
  grid-auto-flow: column;
  column-gap: 24px;
  overflow-x: auto;
  width: 100%;
}

.trip-stops__block-inner {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.plus-block {
  line-height: 1;
}

.reload-stop-list {
  display: flex;
  flex-direction: column;
}

.reload-stop {
  border: 2px dashed #556ee6;
  border-radius: 8px;
  flex: 1;
}
</style>
